import {
  Box,
  Card,
  CardContent,
  alpha,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Avatar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  CircularProgress,
} from "@mui/material";
import React, { useState } from "react";
import { theme } from "../../../../../theme";
import { hasPermission } from "../../../../auth/utils/permission.utils";
import { Permission, UserPermission } from "../UserManagement.interface";
import { Save as SaveIcon } from "@mui/icons-material";

// Simple startCase function to capitalize words
const startCase = (str: string): string => {
  return str
    .split("_")
    .map((word) => word.split(" "))
    .flat()
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

interface IUserPermissionsProps {
  selectedLocationId: string;
  moduleKey: string;
  themeColor: string;
  groupedPermissions: Record<string, Permission[]>;
  mockUsers: Array<{
    _id: string;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    newPermissions?: any;
    newRoles?: any[];
  }>;
  userPermissions: UserPermission[];
  onPermissionToggle: (userId: string, permissionId: string) => void;
  getPermissionIcon: (hasPermission: boolean) => React.ReactNode;
  accountingColors: any;
  systemPermissions: Array<{
    permission_code: string;
    module: string;
    group: string;
    action: string;
    description?: string;
    is_active: boolean;
  }>;
  saving?: boolean;
  pendingChangesCount?: number;
  onSavePermissionChanges?: () => void;
}

export const UserPermissions: React.FC<IUserPermissionsProps> = ({
  selectedLocationId,
  moduleKey,
  themeColor,
  groupedPermissions,
  mockUsers,
  userPermissions,
  onPermissionToggle,
  getPermissionIcon,
  accountingColors,
  systemPermissions,
  saving,
  pendingChangesCount,
  onSavePermissionChanges,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string>("");

  // Helper function to convert 3-part permission to 2-part permission for user storage
  const convertPermissionTo2Part = (permissionCode: string): string => {
    const parts = permissionCode.split(".");
    if (parts.length === 3) {
      // Convert "accounting.vouchers.create" to "vouchers.create"
      return `${parts[1]}.${parts[2]}`;
    }
    return permissionCode; // Return as-is if not 3 parts
  };

  // Helper function to get display name for permission (just the action part)
  const getPermissionDisplayName = (permissionCode: string): string => {
    const parts = permissionCode.split(".");
    if (parts.length === 3) {
      // Get the action part and format it with startCase
      return startCase(parts[2].replace(/_/g, " "));
    } else if (parts.length === 2) {
      // Get the action part and format it with startCase
      return startCase(parts[1].replace(/_/g, " "));
    }
    return startCase(permissionCode.replace(/_/g, " "));
  };

  // Helper function to convert 2-part permission back to 3-part for API calls
  const convertPermissionTo3Part = (
    twoPartPermission: string,
    moduleKey: string
  ): string => {
    if (
      twoPartPermission.includes(".") &&
      !twoPartPermission.startsWith(moduleKey.toLowerCase())
    ) {
      return `${moduleKey.toLowerCase()}.${twoPartPermission}`;
    }
    return twoPartPermission;
  };

  // Process real permissions data and group by category (group field)
  const processRealPermissions = (): Record<string, Permission[]> => {
    if (!systemPermissions || systemPermissions.length === 0) {
      return {};
    }

    // Filter permissions for current module
    const modulePermissions = systemPermissions.filter(
      (perm) =>
        perm.module.toLowerCase() === moduleKey.toLowerCase() && perm.is_active
    );

    // Group by "group" field (which becomes our category)
    const grouped = modulePermissions.reduce((acc, permission) => {
      const category = permission.group;

      if (!acc[category]) {
        acc[category] = [];
      }

      acc[category].push({
        id: permission.permission_code,
        name: getPermissionDisplayName(permission.permission_code),
        description:
          permission.description || `${permission.action} ${permission.group}`,
        category: category,
      });

      return acc;
    }, {} as Record<string, Permission[]>);

    // Sort categories by number of permissions (highest first)
    const sortedGrouped: Record<string, Permission[]> = {};
    const sortedKeys = Object.keys(grouped).sort(
      (a, b) => grouped[b].length - grouped[a].length
    );

    sortedKeys.forEach((key) => {
      sortedGrouped[key] = grouped[key];
    });

    return sortedGrouped;
  };

  // Use real permissions data instead of mock data
  const realGroupedPermissions = processRealPermissions();

  // Get user permissions from the real data structure for selected location
  const getUserPermissionsForLocation = (
    userId: string,
    locationId: string
  ): string[] => {
    const user = mockUsers.find((u) => u._id === userId);
    if (!user?.newPermissions?.[moduleKey]) {
      return [];
    }

    // Extract permissions for the specific location
    const modulePermissions = user.newPermissions[moduleKey];
    if (!Array.isArray(modulePermissions)) {
      return [];
    }

    const locationData = modulePermissions.find(
      (loc: any) => loc.location_id === locationId
    );
    if (
      !locationData?.permissions ||
      !Array.isArray(locationData.permissions)
    ) {
      return [];
    }

    return locationData.permissions;
  };

  // Get user permissions from the real data structure (all locations)
  const getUserPermissions = (userId: string): string[] => {
    const user = mockUsers.find((u) => u._id === userId);
    if (!user?.newPermissions?.[moduleKey]) {
      return [];
    }

    // Extract permissions from all locations for this module
    const modulePermissions = user.newPermissions[moduleKey];
    if (!Array.isArray(modulePermissions)) {
      return [];
    }

    const allPermissions: string[] = [];
    modulePermissions.forEach((locationData: any) => {
      if (locationData.permissions && Array.isArray(locationData.permissions)) {
        allPermissions.push(...locationData.permissions);
      }
    });

    return allPermissions;
  };

  // Get user roles from the real data structure for selected location only
  const getUserRoles = (userId: string): string[] => {
    const user = mockUsers.find((u) => u._id === userId);
    if (!user?.newRoles || !selectedLocationId) {
      return [];
    }

    // Extract role names for current module AND selected location
    return user.newRoles
      .filter(
        (roleData: any) =>
          roleData.module === moduleKey &&
          roleData.location_id === selectedLocationId
      )
      .map((roleData: any) => roleData.role_id || "Unknown Role");
  };

  const checkUserHasPermission = (
    userId: string,
    permissionId: string
  ): boolean => {
    if (!selectedLocationId) {
      return false;
    }

    const user = mockUsers.find((u) => u._id === userId);
    if (!user) {
      return false;
    }

    // Use the updated hasPermission function with the new signature
    return hasPermission(
      permissionId, // Already in 3-part format like "accounting.dashboard.view"
      user.newPermissions, // Use newPermissions structure
      selectedLocationId, // location_id parameter
      false // isStoreOwner parameter - defaulting to false since not available in user type
    );
  };

  // Handle permission toggle - use parent's onPermissionToggle function
  const handlePermissionToggle = (userId: string, permissionId: string) => {
    onPermissionToggle(userId, permissionId);
  };

  const selectedUser = mockUsers.find(
    (user: any) => user._id === selectedUserId
  );
  const selectedUserRoles = selectedUserId ? getUserRoles(selectedUserId) : [];

  // Helper functions for user data
  const getUserDisplayName = (user: any): string => {
    const firstName = user.first_name || "";
    const lastName = user.last_name || "";
    return `${firstName} ${lastName}`.trim() || user.email || "Unknown User";
  };

  const getUserInitials = (user: any): string => {
    const firstName = user.first_name || "";
    const lastName = user.last_name || "";

    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    } else if (firstName) {
      return firstName.charAt(0).toUpperCase();
    } else if (lastName) {
      return lastName.charAt(0).toUpperCase();
    } else if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }

    return "?";
  };

  return (
    <Box>
      {selectedLocationId ? (
        <Box>
          {/* User Selector */}
          <Box sx={{ mb: 3 }}>
            <FormControl sx={{ minWidth: 300 }}>
              <InputLabel>Select User</InputLabel>
              <Select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value as string)}
                label="Select User"
                size="small"
              >
                <MenuItem value="">
                  <em>Select user to view permissions</em>
                </MenuItem>
                {mockUsers.map((user: any) => (
                  <MenuItem key={user._id} value={user._id}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          bgcolor: themeColor,
                          fontSize: "0.75rem",
                        }}
                      >
                        {getUserInitials(user)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {getUserDisplayName(user)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Selected User Info */}
            {selectedUser && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: alpha(themeColor, 0.05),
                  borderRadius: 1,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: themeColor,
                      fontSize: "1rem",
                    }}
                  >
                    {selectedUser ? getUserInitials(selectedUser) : "?"}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {selectedUser
                        ? getUserDisplayName(selectedUser)
                        : "Unknown User"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedUser ? selectedUser.email : ""}
                    </Typography>
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        gap: 0.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ mr: 1, fontWeight: 600 }}
                      >
                        Assigned Roles:
                      </Typography>
                      {selectedUserRoles.length > 0 ? (
                        selectedUserRoles.map((role, index) => (
                          <Chip
                            key={`${role}-${index}`}
                            label={role}
                            size="small"
                            sx={{
                              bgcolor: alpha(themeColor, 0.1),
                              color: themeColor,
                              fontSize: "0.7rem",
                            }}
                          />
                        ))
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No roles assigned
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Stack>
              </Box>
            )}
          </Box>

          {selectedUserId ? (
            Object.keys(realGroupedPermissions).length === 0 ? (
              <Alert
                severity="info"
                sx={{ borderColor: accountingColors.info }}
              >
                <Typography variant="body2">
                  No permissions are available for the{" "}
                  <strong>{moduleKey.toUpperCase()}</strong> module. This could
                  mean:
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  <li>
                    No permissions have been configured for this module yet
                  </li>
                  <li>
                    All permissions for this module are currently inactive
                  </li>
                  <li>There may be a configuration issue</li>
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Please contact your system administrator to configure
                  permissions for this module.
                </Typography>
              </Alert>
            ) : (
              <Box>
                <Card>
                  <CardContent sx={{ p: 0 }}>
                    <Box
                      sx={{
                        p: 3,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        bgcolor: alpha(themeColor, 0.05),
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: themeColor }}
                      >
                        Permissions Matrix for{" "}
                        {selectedUser
                          ? getUserDisplayName(selectedUser)
                          : "Unknown User"}{" "}
                        - {moduleKey.toUpperCase()}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        Click on permission cells to toggle access for this user
                      </Typography>
                    </Box>
                    <TableContainer component={Paper} variant="outlined">
                      <Table sx={{ minWidth: 800 }}>
                        <TableHead>
                          <TableRow sx={{ bgcolor: alpha(themeColor, 0.1) }}>
                            <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>
                              User
                            </TableCell>
                            {Object.keys(realGroupedPermissions).map(
                              (category) => (
                                <TableCell
                                  key={category}
                                  align="center"
                                  sx={{ fontWeight: 600 }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: themeColor,
                                      }}
                                    >
                                      {startCase(category)}
                                    </Typography>
                                  </Box>
                                </TableCell>
                              )
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={2}
                              >
                                <Avatar
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    bgcolor: themeColor,
                                    fontSize: "1rem",
                                  }}
                                >
                                  {selectedUser
                                    ? getUserInitials(selectedUser)
                                    : "?"}
                                </Avatar>
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {selectedUser
                                      ? getUserDisplayName(selectedUser)
                                      : "Unknown User"}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {selectedUser ? selectedUser.email : ""}
                                  </Typography>
                                  {selectedUserRoles.length > 0 && (
                                    <Box
                                      sx={{
                                        mt: 0.5,
                                        display: "flex",
                                        gap: 0.5,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      {selectedUserRoles.map((role) => (
                                        <Chip
                                          key={role}
                                          label={role}
                                          size="small"
                                          sx={{
                                            bgcolor: alpha(themeColor, 0.1),
                                            color: themeColor,
                                            fontSize: "0.6rem",
                                            height: 16,
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              </Stack>
                            </TableCell>
                            {Object.entries(realGroupedPermissions).map(
                              ([category, categoryPermissions]) => (
                                <TableCell key={category} align="center">
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.5,
                                      minHeight: 120,
                                    }}
                                  >
                                    {categoryPermissions.map(
                                      (permission: any) => {
                                        const userHasPermission =
                                          checkUserHasPermission(
                                            selectedUserId,
                                            permission.id
                                          );
                                        return (
                                          <Box
                                            key={permission.id}
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "space-between",
                                              gap: 1,
                                              p: 0.5,
                                              borderRadius: 0.5,
                                              cursor: "pointer",
                                              bgcolor: userHasPermission
                                                ? alpha(themeColor, 0.1)
                                                : "transparent",
                                              border: `1px solid ${alpha(
                                                themeColor,
                                                userHasPermission ? 0.3 : 0.1
                                              )}`,
                                              "&:hover": {
                                                bgcolor: alpha(
                                                  themeColor,
                                                  0.15
                                                ),
                                                borderColor: themeColor,
                                                transform: "translateY(-1px)",
                                                boxShadow: `0 2px 4px ${alpha(
                                                  themeColor,
                                                  0.2
                                                )}`,
                                              },
                                              transition:
                                                "all 0.2s ease-in-out",
                                            }}
                                            onClick={() =>
                                              handlePermissionToggle(
                                                selectedUserId,
                                                permission.id
                                              )
                                            }
                                          >
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                textTransform: "capitalize",
                                                fontSize: "0.7rem",
                                                fontWeight: userHasPermission
                                                  ? 600
                                                  : 400,
                                                color: userHasPermission
                                                  ? themeColor
                                                  : "text.secondary",
                                                textAlign: "left",
                                                lineHeight: 1.2,
                                              }}
                                            >
                                              {permission.name}
                                            </Typography>
                                            <Box
                                              sx={{ ml: 0.5, flexShrink: 0 }}
                                            >
                                              {getPermissionIcon(
                                                userHasPermission
                                              )}
                                            </Box>
                                          </Box>
                                        );
                                      }
                                    )}
                                  </Box>
                                </TableCell>
                              )
                            )}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Permission Summary */}
                    <Box sx={{ p: 3, bgcolor: alpha(themeColor, 0.02) }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, mb: 2, color: themeColor }}
                      >
                        Permission Summary
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        {Object.entries(realGroupedPermissions).map(
                          ([category, categoryPermissions]) => {
                            const categoryUserPermissions =
                              categoryPermissions.filter((permission: any) =>
                                checkUserHasPermission(
                                  selectedUserId,
                                  permission.id
                                )
                              );
                            const percentage = Math.round(
                              (categoryUserPermissions.length /
                                categoryPermissions.length) *
                                100
                            );

                            return (
                              <Box key={category} sx={{ minWidth: 120 }}>
                                <Typography
                                  variant="caption"
                                  sx={{ fontWeight: 600, color: themeColor }}
                                >
                                  {startCase(category)}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mt: 0.5,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      flex: 1,
                                      height: 6,
                                      bgcolor: "grey.200",
                                      borderRadius: 1,
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: `${percentage}%`,
                                        height: "100%",
                                        bgcolor: themeColor,
                                        transition: "width 0.3s ease",
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 600, minWidth: 30 }}
                                  >
                                    {percentage}%
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {categoryUserPermissions.length} of{" "}
                                  {categoryPermissions.length}
                                </Typography>
                              </Box>
                            );
                          }
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {/* Save Button - Bottom Right */}
                {onSavePermissionChanges &&
                  pendingChangesCount &&
                  pendingChangesCount > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 3,
                      }}
                    >
                      <Button
                        variant="contained"
                        startIcon={
                          saving ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <SaveIcon />
                          )
                        }
                        onClick={onSavePermissionChanges}
                        disabled={saving}
                        sx={{
                          bgcolor: "success.main",
                          color: "white",
                          fontWeight: 600,
                          px: 3,
                          py: 1,
                          borderRadius: 2,
                          "&:hover": {
                            bgcolor: "success.dark",
                          },
                          "&:disabled": {
                            bgcolor: "grey.300",
                          },
                        }}
                      >
                        {saving
                          ? "Saving Changes..."
                          : `Save Permission Changes (${pendingChangesCount})`}
                      </Button>
                    </Box>
                  )}
              </Box>
            )
          ) : (
            <Alert severity="info" sx={{ borderColor: accountingColors.info }}>
              Please select a user to view and manage their permissions.
            </Alert>
          )}
        </Box>
      ) : (
        <Alert severity="info" sx={{ borderColor: accountingColors.info }}>
          Please select a location to view the permissions matrix.
        </Alert>
      )}
    </Box>
  );
};
